import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Snackbar, Alert, Button,
  Skeleton, FormControl, Select, MenuItem,
  Collapse, Fade, CircularProgress,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { portfolioAPI } from '../services/api';
import { RegimeBadge, KpiStrip, WatchlistTable, BacktestStats, SectorHeatmap, HistoryPerformance } from '../components/regime-watchlist';

const RegimeWatchlist = () => {
  const { t } = useTranslation();
  // ── State ──
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showBacktest, setShowBacktest] = useState(false);

  // Filters
  const [topN, setTopN] = useState(15);
  const [sector, setSector] = useState('');
  const [highlightSector, setHighlightSector] = useState(null);

  // ── Fetch ──
  const fetchWatchlist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { top_n: topN };
      if (sector) params.sector = sector;
      const res = await portfolioAPI.getRegimeWatchlist(params);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch regime watchlist:', err);
      setError(err.response?.data?.detail || t('regimeWatchlist.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [topN, sector, t]);

  useEffect(() => { fetchWatchlist(); }, [fetchWatchlist]);

  const handleRefresh = async () => {
    await fetchWatchlist();
    setSnackbar({ open: true, message: t('regimeWatchlist.snackbar.updated'), severity: 'success' });
  };

  // ── Unique sectors from response ──
  const sectors = data?.watchlist
    ? [...new Set(data.watchlist.map(w => w.sector_code).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    : [];

  const filteredWatchlist = useMemo(() => {
    const list = data?.watchlist || [];
    return highlightSector ? list.filter(w => w.sector_code === highlightSector) : list;
  }, [data, highlightSector]);

  // ── Loading skeleton ──
  if (loading && !data) {
    const sk = { animation: 'wave' };
    const gridCols = 'minmax(70px,1.3fr) minmax(44px,0.7fr) minmax(80px,1.5fr) minmax(56px,0.9fr) minmax(62px,1.1fr) minmax(52px,0.9fr) minmax(40px,0.7fr) minmax(62px,1.1fr) minmax(62px,1.1fr) minmax(40px,0.7fr) minmax(56px,1fr) minmax(56px,1fr) minmax(24px,0.4fr)';
    // Skeleton column widths paired with stable string ids (avoid array-index keys)
    const skeletonCols = [
      { id: 'col-ticker', w: 44 }, { id: 'col-tier', w: 28 }, { id: 'col-name', w: 52 },
      { id: 'col-sector', w: 36 }, { id: 'col-mcap', w: 40 }, { id: 'col-price', w: 32 },
      { id: 'col-rs', w: 26 }, { id: 'col-vol', w: 40 }, { id: 'col-mflow', w: 40 },
      { id: 'col-eff', w: 26 }, { id: 'col-atr', w: 36 }, { id: 'col-drawd', w: 36 },
      { id: 'col-more', w: 14 },
    ];
    const skeletonRowIds = ['row-a', 'row-b', 'row-c', 'row-d', 'row-e', 'row-f', 'row-g', 'row-h'];
    const skeletonPillIds = ['pill-strength', 'pill-trend', 'pill-momentum', 'pill-flow'];
    const skeletonGuides = [
      { id: 'guide-strategy', mult: 0 },
      { id: 'guide-focus', mult: 1 },
      { id: 'guide-risk', mult: 2 },
      { id: 'guide-note', mult: 3 },
    ];
    const skeletonSectorIds = ['sec-a', 'sec-b', 'sec-c', 'sec-d', 'sec-e', 'sec-f'];
    const rowMul = [0.85, 1.1, 0.75, 0.95, 1, 0.8, 0.9, 1.05, 0.7, 0.88, 1, 0.92, 0.8];

    return (
      <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>

        {/* ── Header skeleton ── */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} {...sk} />
            <Box>
              <Skeleton variant="text" width={170} height={28} {...sk} />
              <Skeleton variant="text" width={240} height={14} {...sk} />
              <Skeleton variant="text" width={80} height={12} {...sk} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 2 }} {...sk} />
            <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 2 }} {...sk} />
            <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2 }} {...sk} />
          </Box>
        </Box>

        {/* ── RegimeBadge skeleton ── */}
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, mb: 2, overflow: 'hidden' }}>
          {/* Row 1: icon + label + chips + count */}
          <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 1, flexShrink: 0 }} {...sk} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={120} height={20} {...sk} />
              <Skeleton variant="text" width={160} height={13} {...sk} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 3 }} {...sk} />
              <Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: 3 }} {...sk} />
            </Box>
            <Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: 3 }} {...sk} />
          </Box>
          {/* Row 2: score pills */}
          <Box sx={{ px: 2, py: 0.8, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
            {skeletonPillIds.map(id => (
              <Skeleton key={id} variant="rounded" width={100} height={28} sx={{ borderRadius: 0.8 }} {...sk} />
            ))}
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 3 }} {...sk} />
            <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 3 }} {...sk} />
          </Box>
          {/* Row 3: guidance */}
          <Box sx={{ px: 2, py: 0.8, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {skeletonGuides.map(({ id, mult }) => (
              <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Skeleton variant="text" width={40} height={12} {...sk} />
                <Skeleton variant="text" width={70 + mult * 10} height={12} {...sk} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── SectorHeatmap skeleton ── */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
            <Skeleton variant="circular" width={14} height={14} {...sk} />
            <Skeleton variant="text" width={80} height={14} {...sk} />
            <Skeleton variant="text" width={100} height={12} {...sk} />
          </Box>
          <Skeleton variant="rounded" height={22} sx={{ borderRadius: 0.5, mb: 0.8 }} {...sk} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0.5 }}>
            {skeletonSectorIds.map(id => (
              <Skeleton key={id} variant="rounded" height={28} sx={{ borderRadius: 0.5 }} {...sk} />
            ))}
          </Box>
        </Box>

        {/* ── WatchlistTable skeleton ── */}
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', mb: 0 }}>
          {/* Table header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, px: 1.5, py: 0.6, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
            {skeletonCols.map(({ id, w }) => (
              <Skeleton key={id} variant="text" width={w} height={12} {...sk} />
            ))}
          </Box>
          {/* Table rows */}
          {skeletonRowIds.map((rowId, r) => (
            <Box key={rowId} sx={{
              display: 'grid', gridTemplateColumns: gridCols, px: 1.5, py: 0.65, alignItems: 'center',
              borderBottom: '1px solid #f1f5f9',
              ...(r % 2 === 1 ? { bgcolor: '#fafbfc' } : {}),
            }}>
              {skeletonCols.map(({ id, w }, i) => (
                <Skeleton
                  key={`${rowId}-${id}`}
                  variant="text"
                  width={w * rowMul[(i + r) % rowMul.length]}
                  height={14}
                  sx={{ borderRadius: 0.3 }}
                  {...sk}
                />
              ))}
            </Box>
          ))}
        </Box>

        {/* ── Backtest toggle skeleton ── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mt: 2, px: 2, py: 1,
          bgcolor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 2.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={15} height={15} {...sk} />
            <Skeleton variant="text" width={140} height={16} {...sk} />
            <Skeleton variant="text" width={120} height={12} {...sk} />
          </Box>
          <Skeleton variant="circular" width={18} height={18} {...sk} />
        </Box>
      </Box>
    );
  }

  return (
    <Fade in={!!data} timeout={400}>
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', position: 'relative' }}>

      {/* Loading overlay for re-fetch */}
      {loading && data && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 1,
        }}>
          <CircularProgress size={32} sx={{ color: '#8b5cf6' }} />
        </Box>
      )}

      {/* ═══════ HEADER ═══════ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
          }}>
            <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
                {t('regimeWatchlist.title')}
              </Typography>
              {data?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#8b5cf6', boxShadow: '0 0 6px #8b5cf6' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {data.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('regimeWatchlist.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              value={topN}
              onChange={(e) => setTopN(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '4px', height: 34, fontSize: 13 }}
            >
              {[10, 15, 20, 30, 50].map(n => (
                <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>Top {n}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '4px', height: 34, fontSize: 13 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}><em>{t('regimeWatchlist.allSectors')}</em></MenuItem>
              {sectors.map(s => (
                <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={handleRefresh} disabled={loading}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {loading ? t('regimeWatchlist.loading') : t('regimeWatchlist.refresh')}
          </Button>
        </Box>
      </Box>

      {/* ═══════ ERROR ═══════ */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.3, fontSize: '0.75rem' }}>{error}</Alert>
      )}

      {/* ═══════ REGIME BADGE ═══════ */}
      {data && (
        <RegimeBadge
          regime={data.regime}
          strategies={data.allowed_strategies || []}
          totalCandidates={data.total_candidates || 0}
          interpretation={data.interpretation}
          regimeDetails={data.regime_details}
          notes={data.notes}
          marketStateLabels={data.market_state_labels}
          selloffOverride={data.selloff_override}
          selloffNote={data.selloff_note}
        />
      )}

      {/* ═══════ KPI STRIP ═══════ */}
      {data?.watchlist && (
        <KpiStrip watchlist={filteredWatchlist} overheatSummary={data.overheat_summary} />
      )}

      {/* ═══════ SECTOR HEATMAP (B2) ═══════ */}
      {data?.sector_distribution && data.sector_distribution.length > 0 && (
        <SectorHeatmap
          sectorDistribution={data.sector_distribution}
          onSectorClick={(s) => setHighlightSector(prev => prev === s ? null : s)}
          selectedSector={highlightSector}
        />
      )}

      {/* ═══════ WATCHLIST TABLE ═══════ */}
      {data && (
        <WatchlistTable
          watchlist={filteredWatchlist}
          regime={data.regime}
          overheatSummary={data.overheat_summary}
        />
      )}

      {/* ═══════ BACKTEST (collapsible) ═══════ */}
      <Box
        onClick={() => setShowBacktest(!showBacktest)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mt: 2, px: 2, py: 1,
          bgcolor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: showBacktest ? '10px 10px 0 0' : 2.5,
          cursor: 'pointer', userSelect: 'none',
          transition: 'border-radius 0.2s',
          '&:hover': { bgcolor: '#f1f5f9' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon sx={{ fontSize: 15, color: '#64748b' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
              {t('regimeWatchlist.backtest.title')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
            {t('regimeWatchlist.backtest.subtitle', { regime: data?.regime ? ` — ${data.regime}` : '' })}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{
          fontSize: 18, color: '#94a3b8',
          transition: 'transform 0.2s',
          transform: showBacktest ? 'rotate(180deg)' : 'none',
        }} />
      </Box>
      <Collapse in={showBacktest}>
        <Box sx={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
          <BacktestStats regime={data?.regime} />
          <Box sx={{ borderTop: '1px solid #e5e7eb' }}>
            <HistoryPerformance />
          </Box>
        </Box>
      </Collapse>

      {/* ═══════ SNACKBAR ═══════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Fade>
  );
};

export default RegimeWatchlist;
